import React, {FC, ReactNode, useRef} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';

/*
 * Props.
 */

interface DraftSaveIndicatorProps {
  status: DraftSaveStatusesEnum;
  isSaving: boolean;
  errorMessage?: ReactNode;
}

export enum DraftSaveStatusesEnum {
  NEVER_SAVED = 'NEVER_SAVED',
  SAVED_OFFLINE = 'SAVED_OFFLINE',
  SAVED = 'SAVED',
  ERROR = 'ERROR',
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  align-self: center;
  margin: 0 10px;

  width: 20px;
  ${center()};
`;

/*
 * Component.
 */

export const DraftSaveIndicator: FC<DraftSaveIndicatorProps> = (props) => {
  const {status, isSaving} = props;

  // Track the status from before we started to save, to avoid showing an offline indicator temporarily.
  const statusUnlessSavingRef = useRef(status);
  if (!isSaving) {
    statusUnlessSavingRef.current = status;
  }
  const statusUnlessSaving = statusUnlessSavingRef.current;

  return <StyledWrapperDiv>{renderStatus(statusUnlessSaving, props.errorMessage)}</StyledWrapperDiv>;
};

function renderStatus(status: DraftSaveStatusesEnum, errorMessage?: ReactNode) {
  const renderErrorIcon = (theme: UserTheme) => {
    if (errorMessage) {
      return (
        <TooltipCoordinator
          isMultiline
          render={() => (
            <>
              <Trans id="draftsaveindicator-warning-tooltip">
                Sending is disabled until this error is fixed:
              </Trans>
              <li>{errorMessage}</li>
            </>
          )}
        >
          <Icon name="warningFilledSmall" color={theme.greys.shade60} />
        </TooltipCoordinator>
      );
    }
    return <Icon name="warningFilledSmall" color={theme.greys.shade60} />;
  };

  switch (status) {
    case DraftSaveStatusesEnum.SAVED_OFFLINE:
      return (
        <UserThemeConsumer>
          {(theme) => (
            <TooltipCoordinator
              render={() => (
                <Trans id="draftsaveindicator-savedoffline-tooltip">
                  Your changes will be synchronized when you&apos;re back online
                </Trans>
              )}
            >
              <Icon name="offlineFilledSmall" color={theme.greys.shade60} />
            </TooltipCoordinator>
          )}
        </UserThemeConsumer>
      );

    case DraftSaveStatusesEnum.ERROR:
      return <UserThemeConsumer>{renderErrorIcon}</UserThemeConsumer>;

    default:
      return null;
  }
}
